/**
 * Base path for doctor e-prescription hub and related screens — the
 * canonical path. routes.tsx also keeps the old `/eprescription` (no
 * hyphen) paths registered as `<Navigate replace>` redirects to this one,
 * for any bookmarked/cached links from before this constant existed.
 */
export const E_PRESCRIPTIONS_BASE = "/e-prescriptions";

/**
 * Maps API `PrescriptionStatus` to UI labels + a real color per status
 * (order fulfillment lifecycle). `pending`/`cancelled` used to map to
 * antd's `"default"` Tag color, which isn't actually a color at all — it's
 * antd's name for "plain bordered, no fill", so both rendered as an
 * uncolored outline no matter what. Every status below now resolves to an
 * actually-visible color: antd's named presets where those already read
 * clearly (processing/blue, warning/gold, success/green, etc.), explicit
 * hex for the states that had none.
 */
export const doctorPrescriptionStatusConfig: Record<
  string,
  { color: string; label: string; servedHint?: string }
> = {
  pending: { color: "#94a3b8", label: "Pending", servedHint: "Not yet served" },
  processing: {
    color: "processing",
    label: "Processing",
    servedHint: "Fulfillment in progress",
  },
  awaiting_payment: {
    color: "warning",
    label: "Awaiting payment",
    servedHint: "Waiting for payment before dispatch",
  },
  awaiting_shipping: {
    color: "cyan",
    label: "Awaiting shipping",
    servedHint: "Paid; preparing delivery",
  },
  shipped: {
    color: "blue",
    label: "Shipped",
    servedHint: "On the way to the patient",
  },
  partially_fulfilled: {
    color: "geekblue",
    label: "Partially fulfilled",
    servedHint: "Some items served; rest in progress",
  },
  completed: {
    color: "success",
    label: "Delivered",
    servedHint: "Order served to the patient",
  },
  rejected: { color: "error", label: "Rejected", servedHint: "Order not fulfilled" },
  cancelled: { color: "#78716c", label: "Cancelled", servedHint: "Order cancelled" },
  exceeded: {
    color: "#b45309",
    label: "Exceeded",
    servedHint: "Order could not be completed as placed",
  },
};

/**
 * Shared status → color/label map for lab requests and X-Ray requests
 * (both are `Diagnostic` rows, differentiated only by `requestType`). Used
 * to live as two independent copies, one hand-maintained inside each of
 * labrequest-list.tsx and x-raylist.tsx — same drift risk as the sidebar
 * nav list before that got a single shared source of truth.
 */
export const diagnosticStatusConfig: Record<
  string,
  { color: string; label: string }
> = {
  pending: { color: "#94a3b8", label: "Pending" },
  scheduled: { color: "processing", label: "Scheduled" },
  processing: { color: "processing", label: "Processing" },
  awaiting_payment: { color: "warning", label: "Awaiting payment" },
  awaiting_results: { color: "cyan", label: "Awaiting results" },
  result_updated: { color: "blue", label: "Result ready" },
  served: { color: "success", label: "Served" },
  rejected: { color: "error", label: "Rejected" },
  cancelled: { color: "#78716c", label: "Cancelled" },
};
